import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useUnreadMessages } from "../context/UnreadMessagesContext";
import { Briefcase, Menu, X, ChevronDown, User, LayoutDashboard, LogOut, Globe, MessageSquare, Eye, Building2 } from "lucide-react";
import { CandidateAvatar } from "./CandidateAvatar";

const LOGO_URL = "/logos/official/infocityjobs.png";

const LANGUAGES = [
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const { unreadCount } = useUnreadMessages();
  const unreadBadge = unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const dashboardPath = user?.role === "employer" ? "/company/dashboard" : "/dashboard";

  const handleLogout = () => {
    // Navigate first and wait a full paint cycle (double rAF) before clearing
    // the user — avoids racing a mounted page's own !user redirect effect.
    setUserMenuOpen(false);
    navigate("/", { replace: true });
    requestAnimationFrame(() => requestAnimationFrame(() => logout()));
  };

  const currentLang = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
  // Guests, candidates AND employers now get the same MobileHeader app shell on
  // mobile (same logo scale/height/hamburger/language pattern everywhere) —
  // desktop navbar is untouched for everyone.
  const hideOnMobile = !user || user?.role === "candidate" || user?.role === "employer";

  return (
    <nav
      data-testid="navbar"
      className={`sticky top-0 z-50 w-full border-b border-slate-200 bg-white shadow-sm ${hideOnMobile ? "hidden lg:block" : ""}`}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">

          {/* Logo — official PNG canvas is 1200×320 with visible content at rows 50–268
              (left starts at x=26, content width 859px for InfoCityJobs). We use CSS
              aspect-ratio + absolute positioning derived from those exact measurements
              to render ONLY the visible icon+wordmark, precisely centered, never cropped. */}
          <Link
            to={user?.role === "employer" ? "/company/dashboard" : "/"}
            data-testid="navbar-logo"
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              height: "54px",
              aspectRatio: "859 / 218",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img
              src={LOGO_URL}
              alt="InfoCityJobs"
              style={{
                position: "absolute",
                top: "-22.94%",
                left: "-3.03%",
                height: "146.79%",
                width: "139.7%",
                maxWidth: "none",
                display: "block",
              }}
            />
          </Link>

          {/* Desktop Navigation — role-aware. Employers use the CompanyDashboard
              Sidebar as their ONLY primary nav now (Resumen/Publicar/Mis
              Anuncios/Candidatos/Mensajes/Empresa) — the header renders nothing
              extra for them (avoids the old center-of-header duplicate link). */}
          <div className="hidden lg:flex items-center gap-1">
            {user?.role !== "employer" && (
              <>
                <Link to="/" data-testid="nav-home"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive("/") ? "text-[#028CF8] bg-[#028CF8]/10" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                  {t("nav.home")}
                </Link>
                <Link to="/jobs" data-testid="nav-jobs"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive("/jobs") ? "text-[#028CF8] bg-[#028CF8]/10" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                  {t("nav.findJobs")}
                </Link>
                <Link to="/companies" data-testid="nav-companies"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive("/companies") ? "text-[#028CF8] bg-[#028CF8]/10" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                  {t("nav.companies")}
                </Link>
              </>
            )}
            {user?.role === "candidate" && (
              <Link to="/cv-builder" data-testid="nav-cv-builder"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === "/cv-builder" ? "text-[#028CF8] bg-[#028CF8]/10" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                {t("userDash.createCV")}
              </Link>
            )}
          </div>


          {/* Right: Language switcher + Auth */}
          <div className="hidden lg:flex items-center gap-2">

            {/* Language switcher */}
            <div className="relative">
              <button
                data-testid="language-switcher"
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <Globe className="h-4 w-4 text-slate-400" />
                <span className="uppercase font-semibold text-xs tracking-wide">{currentLang.code}</span>
                <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-50">
                  {LANGUAGES.map(({ code, label, flag }) => (
                    <button
                      key={code}
                      data-testid={`lang-${code}`}
                      onClick={() => { changeLanguage(code); setLangOpen(false); }}
                      className={`flex items-center gap-2.5 w-full px-4 py-2.5 text-sm transition-colors ${language === code ? "text-[#028CF8] font-semibold bg-[#028CF8]/5" : "text-slate-700 hover:bg-slate-50"}`}
                    >
                      <span className="text-base">{flag}</span>
                      {label}
                      {language === code && (
                        <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#028CF8]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Auth buttons */}
            {user ? (
              <div className="relative">
                <button
                  data-testid="user-menu-button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <span className="relative flex-shrink-0">
                    <CandidateAvatar name={user.name} picture={user.picture} size={28} />
                    {unreadBadge && (
                      <span data-testid="user-avatar-messages-badge"
                        className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#F55FA5] text-white text-[9px] font-bold flex items-center justify-center leading-none ring-2 ring-white">
                        {unreadBadge}
                      </span>
                    )}
                  </span>
                  <span className="text-sm font-medium text-slate-700 max-w-24 truncate">{user.name}</span>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-xl py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <p className="text-xs text-slate-500">{t("nav.signedInAs")}</p>
                      <p className="text-sm font-medium text-slate-900 truncate">{user.email}</p>
                    </div>
                    <Link to={dashboardPath} onClick={() => setUserMenuOpen(false)} data-testid="user-menu-dashboard"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                      <LayoutDashboard className="h-4 w-4" /> {t("nav.dashboard")}
                    </Link>
                    {user.role === "employer" ? (
                      <>
                        <Link to="/company/dashboard?tab=company" onClick={() => setUserMenuOpen(false)} data-testid="user-menu-company-profile"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                          <Building2 className="h-4 w-4" /> {t("tabs.companyProfile")}
                        </Link>
                        <Link to="/messages" onClick={() => setUserMenuOpen(false)} data-testid="user-menu-messages"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                          <MessageSquare className="h-4 w-4" /> {t("bottomNav.messages")}
                          {unreadBadge && (
                            <span data-testid="user-menu-messages-badge"
                              className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#F55FA5] text-white text-[10px] font-bold flex items-center justify-center leading-none">
                              {unreadBadge}
                            </span>
                          )}
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link to={dashboardPath} onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                          <User className="h-4 w-4" /> {t("nav.profile")}
                        </Link>
                        <Link to="/messages" onClick={() => setUserMenuOpen(false)} data-testid="user-menu-messages"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                          <MessageSquare className="h-4 w-4" /> {t("bottomNav.messages")}
                          {unreadBadge && (
                            <span data-testid="user-menu-messages-badge"
                              className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#F55FA5] text-white text-[10px] font-bold flex items-center justify-center leading-none">
                              {unreadBadge}
                            </span>
                          )}
                        </Link>
                        <Link to="/dashboard?tab=whoviewedme" onClick={() => setUserMenuOpen(false)} data-testid="user-menu-whoviewedme"
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                          <Eye className="h-4 w-4" /> {t("bottomNav.whoViewedMe")}
                        </Link>
                      </>
                    )}
                    <button onClick={handleLogout} data-testid="logout-button"
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                      <LogOut className="h-4 w-4" /> {t("nav.signOut")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/auth" data-testid="nav-login"
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-900 hover:bg-slate-50 font-semibold text-sm transition-all">
                  {t("nav.signIn")}
                </Link>
                <Link to="/auth?tab=register" data-testid="nav-register"
                  className="px-4 py-2 rounded-lg bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold text-sm transition-all shadow-md">
                  {t("nav.getStarted")}
                </Link>
              </>
            )}
          </div>

          {/* Mobile: language + hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            <button
              onClick={() => setLangOpen(!langOpen)}
              data-testid="mobile-lang-switcher"
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 uppercase tracking-wide"
            >
              {currentLang.flag} {currentLang.code}
            </button>
            {langOpen && (
              <div className="absolute right-4 top-14 w-40 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-50">
                {LANGUAGES.map(({ code, label, flag }) => (
                  <button key={code} onClick={() => { changeLanguage(code); setLangOpen(false); }}
                    className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm ${language === code ? "text-[#028CF8] font-semibold" : "text-slate-700"}`}>
                    <span>{flag}</span> {label}
                  </button>
                ))}
              </div>
            )}
            <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-button">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 py-4 space-y-1">
            {[
              { path: "/", label: t("nav.home") },
              { path: "/jobs", label: t("nav.findJobs") },
              { path: "/companies", label: t("nav.companies") },
            ].map(({ path, label }) => (
              <Link key={path} to={path} onClick={() => setMobileOpen(false)}
                className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                {label}
              </Link>
            ))}
            <div className="pt-2 border-t border-slate-200 space-y-2">
              {user ? (
                <>
                  <Link to={dashboardPath} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm font-semibold text-[#028CF8]">
                    {t("nav.dashboard")}
                  </Link>
                  <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-red-600">
                    {t("nav.signOut")}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm text-slate-700">{t("nav.signIn")}</Link>
                  <Link to="/auth?tab=register" onClick={() => setMobileOpen(false)} className="block mx-4 py-2 text-center text-sm font-semibold bg-[#028CF8] text-white rounded-lg">
                    {t("nav.getStarted")}
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
